import {
  BidStatusTransitionTypeDto,
  type BuyerBidDetailDto,
  type BidStatusTransitionDto,
} from "../../../autogen/bff-api";

export type BidActivityType = "submitted" | "opened" | "rejected" | "rejectionRevoked" | "awarded";

export interface BidActivityEvent {
  type: BidActivityType;
  at: string;
  by?: string | null;
}

const typeOrder: Record<BidActivityType, number> = {
  submitted: 0,
  opened: 1,
  rejected: 2,
  rejectionRevoked: 3,
  awarded: 4,
};

const compareInstants = (a: string, b: string): number => Date.parse(a) - Date.parse(b);

export const latestTransition = (
  transitions: BidStatusTransitionDto[] | undefined,
  type: BidStatusTransitionTypeDto,
): BidStatusTransitionDto | undefined =>
  [...(transitions ?? [])]
    .filter((transition) => transition.type === type)
    .sort((a, b) => compareInstants(b.at, a.at))[0];

export const getBidActivityEvents = (detail: BuyerBidDetailDto): BidActivityEvent[] => {
  const transitions = detail.bid.statusHistory ?? [];
  const submittedAt =
    latestTransition(transitions, BidStatusTransitionTypeDto.Submitted)?.at ?? detail.bid.submittedAt;
  const rejectedAt = latestTransition(transitions, BidStatusTransitionTypeDto.Rejected)?.at;
  const rejectionRevokedAt = latestTransition(transitions, BidStatusTransitionTypeDto.RejectionRevoked)?.at;
  const awardedAt = latestTransition(transitions, BidStatusTransitionTypeDto.Awarded)?.at;

  const events: BidActivityEvent[] = [
    submittedAt ? { type: "submitted", at: submittedAt } : null,
    detail.bidOpenedAt ? { type: "opened", at: detail.bidOpenedAt } : null,
    rejectedAt ? { type: "rejected", at: rejectedAt } : null,
    rejectionRevokedAt
      ? { type: "rejectionRevoked", at: rejectionRevokedAt, by: detail.bid.rejectionRevokedBy ?? null }
      : null,
    awardedAt ? { type: "awarded", at: awardedAt } : null,
  ].filter((event): event is BidActivityEvent => event != null);

  return events.sort((a, b) => {
    const byTimestamp = compareInstants(a.at, b.at);
    return byTimestamp !== 0 ? byTimestamp : typeOrder[a.type] - typeOrder[b.type];
  });
};
